import React from "react";
import { Box } from "@chakra-ui/react";
import { FileCard as FileCardLegacy } from "components/RFQ/FileCard";
import { RfxClientDocument } from "newui/features/Rfx/types/rfx.types";

interface DocumentAttachmentFile {
	name: string;
	file_size: number;
	path: string;
}

// FileCard is an unmigrated JS component whose inferred props are all required.
const FileCard = FileCardLegacy as React.ComponentType<{
	file: DocumentAttachmentFile;
	isPreview?: boolean;
}>;

interface DocumentCategoryOption {
	label: string;
	value: number;
}

interface DocumentAttachmentsListProps {
	/** Category options from the RFx form. When supplied, labels resolve by
	 * categoryId so headings match the rest of the procurer UI; the bidder
	 * screen omits it and falls back to the server-provided categoryName. */
	documentCategories?: DocumentCategoryOption[];
	documentDescription?: string;
	documentFilter?: number | null;
	data?: RfxClientDocument[] | null;
	/** The procurer RFx-preview screen frames the list in a bordered panel and
	 * uses larger category headings to match its description box; the bidder
	 * submission preview renders unframed with smaller headings. */
	variant?: "bidder" | "preview";
}

const UNCATEGORISED_LABEL = "Uncategorised";

export const DocumentAttachmentsList = ({
	data,
	documentFilter,
	documentDescription,
	documentCategories,
	variant = "bidder",
}: DocumentAttachmentsListProps) => {
	const isPreviewVariant = variant === "preview";
	const documentList = documentFilter
		? (data ?? []).filter((item) => item.categoryId === documentFilter)
		: (data ?? []);

	const resolveCategoryLabel = (document: RfxClientDocument): string => {
		if (documentCategories?.length) {
			const match = documentCategories.find(
				(category) => category.value === document.categoryId
			);

			if (match?.label) {
				return match.label;
			}
		}

		return document.categoryName || UNCATEGORISED_LABEL;
	};

	const documents = documentList.reduce<Record<string, DocumentAttachmentFile[]>>(
		(result, document) => {
			const category = resolveCategoryLabel(document);
			const fileData: DocumentAttachmentFile = {
				name: document.fileName,
				file_size: document.fileSize,
				path: document.documentPath,
			};

			if (!result[category]) {
				result[category] = [];
			}

			result[category].push(fileData);

			return result;
		},
		{}
	);

	const categories = Object.keys(documents);

	const list = (
		<>
			{categories.length === 0 && (
				<p>No additional documentation has been provided for this RFx</p>
			)}
			{categories.map((category) => {
				return (
					<div
						className="pb-6"
						key={category}
					>
						<h1
						className={
							isPreviewVariant
								? "text-xl font-bold"
								: "text-lg font-bold"
						}
					>
						{category}
					</h1>
						{documents[category].map((document) => {
							return (
								<FileCard
									key={`${category}-${document.path}-${document.name}`}
									file={document}
									isPreview
								/>
							);
						})}
					</div>
				);
			})}
		</>
	);

	return (
		<>
			{documentDescription && (
				<Box className="mb-8 grid min-h-40 w-full rounded-md border p-10 shadow-lg">
					<h1 className="text-xl font-bold">Documentation Description</h1>
					<p
						className="mt-3 [&>ol]:ml-5 [&>ol]:!list-decimal [&>ul]:ml-5 [&>ul]:!list-disc"
						dangerouslySetInnerHTML={{ __html: documentDescription }}
					/>
				</Box>
			)}
			{isPreviewVariant ? (
				<Box className="grid min-h-80 w-full rounded-md border p-10 shadow-xl">
					{list}
				</Box>
			) : (
				list
			)}
		</>
	);
};
